import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from 'react';
import type { ReactNode } from 'react';
import { createAuthProvider } from './index';
import type { IAuthProvider, LogoutOptions, User, UserRole } from '../types/auth.types';
import { isPlatformRole } from '../types/auth.types';
import { PERMISSIONS, type PermissionName } from '../constants/permissions';
import { tokenService } from '../services/tokenService';
import { userService } from '../services/userService';
import type { MeResponse } from '../services/userService';
import {
  permissionService,
  type ResolvedInstitutionPermissions,
} from '../services/permissionService';
import type { WorkspaceDescriptor } from '../types/workspace.types';
import { buildAdminProfileError, normalizeAuthError } from './authErrorUtils';
import {
  normalizeInstitutionContextId,
  readStoredInstitutionContextId,
  writeStoredInstitutionContextId,
} from './institutionContext';
import {
  useWorkspaceAuthorization,
  type WorkspaceAuthorizationUser,
} from './useWorkspaceAuthorization';
import { cancelWorkspaceQueries, clearWorkspaceCaches } from './workspaceCaches';

export { resolveSelectedWorkspaceKey } from './useWorkspaceAuthorization';

type ExtendedUser = WorkspaceAuthorizationUser;

interface AuthContextValue {
  user: ExtendedUser | null;
  originalUser: ExtendedUser | null;
  loading: boolean;
  error: Error | null;
  login: () => Promise<void>;
  logout: (options?: LogoutOptions) => Promise<void>;
  getAccessToken: () => Promise<string | null>;
  isAuthenticated: boolean;
  // Institution context switching (platform-admin only)
  assumedRole: UserRole | null;
  assumedInstitutionId: string | null;
  assumedInstitutionName: string | null;
  assumeRole: (role: UserRole, institutionId: string, institutionName?: string) => void;
  resetAssumedRole: () => void;
  effectiveRole: UserRole | undefined;
  effectiveInstitutionId: string | undefined;
  institutionSwitcherEnabled: boolean;
  // Permission helpers — no bypass, checked from the permissions array
  permissions: string[];
  permissionsLoading: boolean;
  hasPermission: (permission: PermissionName) => boolean;
  hasAnyPermission: (permissions: PermissionName[]) => boolean;
  hasAllPermissions: (permissions: PermissionName[]) => boolean;
  refetchPermissions: () => Promise<void>;
  workspaces: WorkspaceDescriptor[];
  workspacesLoading: boolean;
  workspaceSwitching: boolean;
  workspaceError: string | null;
  selectedWorkspaceKey: string | null;
  selectWorkspace: (workspace: WorkspaceDescriptor) => Promise<boolean>;
  refetchWorkspaces: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const ASSUMABLE_CONTEXT_ROLES: UserRole[] = [
  'org-admin',
  'org-manager',
  'org-viewer',
];

const ASSUMED_CONTEXT_STORAGE_KEYS = {
  role: 'assumedRole',
  institutionId: 'assumedInstitutionId',
  institutionName: 'assumedInstitutionName',
} as const;

const clearAssumedContextStorage = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  sessionStorage.removeItem(ASSUMED_CONTEXT_STORAGE_KEYS.role);
  sessionStorage.removeItem(ASSUMED_CONTEXT_STORAGE_KEYS.institutionId);
  sessionStorage.removeItem(ASSUMED_CONTEXT_STORAGE_KEYS.institutionName);
};

export const resolveInstitutionSwitcherEnabled = (
  isDevelopment: boolean,
  rawFlag?: string,
): boolean => {
  if (isDevelopment) {
    return true;
  }

  if (!rawFlag) {
    return false;
  }

  return ['1', 'true', 'yes', 'on'].includes(rawFlag.trim().toLowerCase());
};

type InstitutionSwitcherUser = Pick<User, 'role' | 'permissions'> & {
  originalRole?: UserRole;
};

export const canUseInstitutionSwitcher = (
  user: InstitutionSwitcherUser | null | undefined,
  enabled: boolean,
): boolean => {
  if (!enabled || !user) {
    return false;
  }

  const baseRole = user.originalRole ?? user.role;
  if (baseRole === 'platform-admin') {
    return true;
  }

  return Boolean(user.permissions?.includes(PERMISSIONS.PLATFORM_INSTITUTIONS_MANAGE));
};

const validateAssumedRole = (role: string | null): UserRole | null => {
  if (!role) return null;
  if (ASSUMABLE_CONTEXT_ROLES.includes(role as UserRole)) {
    return role as UserRole;
  }
  clearAssumedContextStorage();
  return null;
};

const getSelectedInstitutionId = (availableInstitutions: string[]): string | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  const currentInstitutionId = readStoredInstitutionContextId('institutionId');
  if (currentInstitutionId && availableInstitutions.includes(currentInstitutionId)) {
    return currentInstitutionId;
  }

  return undefined;
};

export const resolveProfileInstitutionId = (
  role: UserRole,
  availableInstitutions: string[],
): string | undefined => {
  if (isPlatformRole(role)) {
    return undefined;
  }

  return getSelectedInstitutionId(availableInstitutions);
};

const syncInstitutionContext = (institutionId?: string): void => {
  if (typeof window === 'undefined') {
    return;
  }

  if (institutionId) {
    writeStoredInstitutionContextId('institutionId', institutionId);
    return;
  }

  sessionStorage.removeItem('institutionId');
};

type GetMeResponse = Awaited<ReturnType<typeof userService.getMe>>;

const getBackendErrorMessage = (response: GetMeResponse): string | undefined => {
  if (!response.data || typeof response.data !== 'object' || !('message' in response.data)) {
    return undefined;
  }

  return typeof response.data.message === 'string' ? response.data.message : undefined;
};

/**
 * Enrich the MSAL user with backend profile data from /admin-users/me.
 * The backend profile is the authorization source of truth, so auth must fail
 * closed when the profile cannot be loaded.
 */
async function enrichWithBackendProfile(msalUser: User): Promise<ExtendedUser> {
  const resp = await userService.getMe();
  if (!resp.ok) {
    throw buildAdminProfileError(resp.status, getBackendErrorMessage(resp));
  }

  const me = resp.data as MeResponse;
  // Platform users can have institution assignments, but they should not
  // automatically enter institution-scoped context unless they explicitly
  // assume a role for that institution.
  let institutionId = resolveProfileInstitutionId(me.role, me.institutions);
  let role = me.role;
  let permissions = me.permissions || [];

  if (institutionId) {
    try {
      const resolved = await permissionService.resolveAuthenticatedUserPermissions(
        me.id,
        institutionId,
      );
      role = resolved.role;
      permissions = Object.entries(resolved.permissions)
        .filter(([, granted]) => granted)
        .map(([permission]) => permission);
    } catch (error) {
      console.warn('Stored institution workspace could not be restored', error);
      institutionId = undefined;
      permissions = [];
    }
  }

  syncInstitutionContext(institutionId);

  const displayName =
    me.display_name ||
    [me.first_name, me.last_name].filter(Boolean).join(' ') ||
    msalUser.name;

  return {
    ...msalUser,
    id: me.id,
    email: me.email,
    name: displayName,
    role,
    permissions,
    institutionId,
    institutions: me.institutions,
  };
}

interface AuthProviderProps {
  children: ReactNode;
  provider?: IAuthProvider;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children, provider }) => {
  const authProvider = useMemo(() => provider || createAuthProvider(), [provider]);

  const [user, setUser] = useState<ExtendedUser | null>(null);
  const [accountUser, setAccountUser] = useState<ExtendedUser | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);
  const [assumedRole, setAssumedRole] = useState<UserRole | null>(null);
  const [assumedInstitutionId, setAssumedInstitutionId] = useState<string | null>(null);
  const [assumedInstitutionName, setAssumedInstitutionName] = useState<string | null>(null);
  const institutionSwitcherEnabled = useMemo(
    () => resolveInstitutionSwitcherEnabled(
      import.meta.env.DEV,
      import.meta.env.VITE_ENABLE_ROLE_TESTING,
    ),
    [],
  );

  const [permissions, setPermissions] = useState<string[]>([]);
  const [permissionsLoading, setPermissionsLoading] = useState<boolean>(false);
  const [hasCanonicalPermissions, setHasCanonicalPermissions] = useState(false);

  const permissionRequestRef = useRef(0);

  const clearAssumedContext = useCallback((): void => {
    setAssumedRole(null);
    setAssumedInstitutionId(null);
    setAssumedInstitutionName(null);
    clearAssumedContextStorage();
  }, []);

  // ── Computed user with role overrides ──
  const computedUser = useMemo((): ExtendedUser | null => {
    if (!user) return null;
    if (assumedRole && assumedInstitutionId) {
      return {
        ...user,
        role: assumedRole,
        institutionId: assumedInstitutionId,
        originalRole: user.role,
        originalInstitutionId: user.institutionId,
      };
    }
    return user;
  }, [user, assumedRole, assumedInstitutionId]);

  const commitInstitutionWorkspace = useCallback((
    originalAccountUser: ExtendedUser,
    resolved: ResolvedInstitutionPermissions,
  ): void => {
    const resolvedPermissions = Object.entries(resolved.permissions)
      .filter(([, granted]) => granted)
      .map(([permission]) => permission);
    const institutionUser: ExtendedUser = {
      ...originalAccountUser,
      role: resolved.role,
      institutionId: resolved.institutionId,
      permissions: resolvedPermissions,
    };

    clearWorkspaceCaches();
    permissionRequestRef.current += 1;
    setUser(institutionUser);
    setPermissions(resolvedPermissions);
    setPermissionsLoading(false);
    setHasCanonicalPermissions(true);
    clearAssumedContext();
    syncInstitutionContext(resolved.institutionId);
  }, [clearAssumedContext]);

  const commitPlatformWorkspace = useCallback((
    originalAccountUser: ExtendedUser,
  ): void => {
    const platformPermissions = originalAccountUser.permissions || [];
    const hasPlatformPermissions = platformPermissions.length > 0;

    clearWorkspaceCaches();
    permissionRequestRef.current += 1;
    setUser({
      ...originalAccountUser,
      institutionId: undefined,
    });
    setPermissions(platformPermissions);
    setPermissionsLoading(!hasPlatformPermissions);
    setHasCanonicalPermissions(hasPlatformPermissions);
    clearAssumedContext();
    syncInstitutionContext(undefined);
  }, [clearAssumedContext]);

  const workspaceAuthorization = useWorkspaceAuthorization({
    effectiveRole: computedUser?.role,
    effectiveInstitutionId: computedUser?.institutionId,
    onInstitutionCommitted: commitInstitutionWorkspace,
    onPlatformCommitted: commitPlatformWorkspace,
  });
  const {
    workspaces,
    workspacesLoading,
    workspaceSwitching,
    workspaceError,
    selectedWorkspaceKey,
    selectWorkspace,
    refetchWorkspaces,
    setAuthenticatedWorkspaceUser,
    clearAuthenticatedWorkspaceUser,
  } = workspaceAuthorization;

  const establishAuthenticatedUser = useCallback((
    authenticatedUser: ExtendedUser,
  ): void => {
    const hasSelectedWorkspace =
      isPlatformRole(authenticatedUser.role) || Boolean(authenticatedUser.institutionId);
    const workspaceUser = hasSelectedWorkspace
      ? authenticatedUser
      : { ...authenticatedUser, permissions: [] };

    permissionRequestRef.current += 1;
    setAccountUser(authenticatedUser);
    setUser(workspaceUser);
    setPermissions(workspaceUser.permissions || []);
    setHasCanonicalPermissions(false);
    setAuthenticatedWorkspaceUser(authenticatedUser);
  }, [setAuthenticatedWorkspaceUser]);

  const clearAuthenticatedState = useCallback((): void => {
    permissionRequestRef.current += 1;
    setUser(null);
    setAccountUser(null);
    setPermissions([]);
    setPermissionsLoading(false);
    setHasCanonicalPermissions(false);
    clearAssumedContext();
    setError(null);
    clearWorkspaceCaches();
    tokenService.clearCache();
    sessionStorage.removeItem('isAuthenticated');
    syncInstitutionContext(undefined);
    clearAuthenticatedWorkspaceUser();
  }, [clearAssumedContext, clearAuthenticatedWorkspaceUser]);

  // ── Restore assumed role from session ──
  useEffect(() => {
    const savedRole = validateAssumedRole(sessionStorage.getItem(ASSUMED_CONTEXT_STORAGE_KEYS.role));
    const savedInst = readStoredInstitutionContextId(ASSUMED_CONTEXT_STORAGE_KEYS.institutionId);
    const savedInstitutionName = sessionStorage.getItem(ASSUMED_CONTEXT_STORAGE_KEYS.institutionName);
    if (savedRole && savedInst) {
      setAssumedRole(savedRole);
      setAssumedInstitutionId(savedInst);
      setAssumedInstitutionName(savedInstitutionName);
    } else if (savedRole) {
      clearAssumedContextStorage();
    }
  }, []);

  const fetchPermissions = useCallback(async (): Promise<void> => {
    const requestId = permissionRequestRef.current + 1;
    permissionRequestRef.current = requestId;
    const isCurrentRequest = (): boolean => permissionRequestRef.current === requestId;

    if (!computedUser) {
      if (isCurrentRequest()) {
        setPermissions([]);
        setPermissionsLoading(false);
      }
      return;
    }

    const directPermissions = computedUser.permissions || [];

    if (
      !assumedRole
      && !isPlatformRole(computedUser.role)
      && !computedUser.institutionId
    ) {
      if (isCurrentRequest()) {
        setPermissions([]);
        setPermissionsLoading(false);
      }
      return;
    }

    if (!assumedRole && (hasCanonicalPermissions || directPermissions.length > 0)) {
      if (isCurrentRequest()) {
        setPermissions(directPermissions);
        setPermissionsLoading(false);
      }
      return;
    }

    // Fetch permissions from the backend when the flattened profile is empty.
    setPermissionsLoading(true);
    try {
      const permissionSetToArray = (permissionSet: Record<string, boolean>): string[] =>
        Object.entries(permissionSet)
          .filter(([, granted]) => granted)
          .map(([permission]) => permission);

      let permArray: string[] = [];

      if (assumedRole) {
        const rolePerms = await permissionService.getRolePermissions(assumedRole, {
          usePlatformContext: true,
        });
        permArray = permissionSetToArray(rolePerms);
      } else if (computedUser.institutionId) {
        const userPerms = await permissionService.getUserPermissions(
          computedUser.id,
          computedUser.institutionId,
        );
        permArray = permissionSetToArray(userPerms);
      } else {
        const rolePerms = await permissionService.getRolePermissions(computedUser.role);
        permArray = permissionSetToArray(rolePerms);
      }

      if (isCurrentRequest()) {
        setPermissions(permArray);
      }
    } catch (error) {
      console.error('Failed to load permissions from backend:', error);
      if (isCurrentRequest()) {
        setPermissions([]);
      }
    } finally {
      if (isCurrentRequest()) {
        setPermissionsLoading(false);
      }
    }
  }, [computedUser, assumedRole, hasCanonicalPermissions]);

  // Reload permissions when user/role changes
  useEffect(() => {
    fetchPermissions();
  }, [computedUser?.id, computedUser?.role, fetchPermissions]);

  // ── Permission checks — no role bypass, purely array-based ──
  const hasPermission = useCallback(
    (permission: PermissionName): boolean => permissions.includes(permission),
    [permissions],
  );

  const hasAnyPermission = useCallback(
    (perms: PermissionName[]): boolean => perms.some(p => permissions.includes(p)),
    [permissions],
  );

  const hasAllPermissions = useCallback(
    (perms: PermissionName[]): boolean => perms.every(p => permissions.includes(p)),
    [permissions],
  );

  // ── Initialize auth & wire tokenService ──
  useEffect(() => {
    tokenService.initialize(authProvider);

    const initializeAuth = async (): Promise<void> => {
      try {
        setLoading(true);

        // Handle redirect callback first
        const redirectResult = await authProvider.handleCallback();
        if (redirectResult) {
          tokenService.setAccessToken(redirectResult.accessToken);
          const enriched = await enrichWithBackendProfile(redirectResult.user);
          establishAuthenticatedUser(enriched);
          setLoading(false);
          return;
        }

        // Check for existing session
        const existingUser = await authProvider.getUser();
        if (existingUser) {
          try {
            const accessToken = await authProvider.getAccessToken();
            tokenService.setAccessToken(accessToken);
          } catch {
            tokenService.setAccessToken(null);
          }
          const enriched = await enrichWithBackendProfile(existingUser);
          establishAuthenticatedUser(enriched);
        }
      } catch (err) {
        clearAuthenticatedState();
        const normalizedError = normalizeAuthError(err);
        const msg = normalizedError.message;
        if (!msg.includes('No accounts found')) {
          setError(normalizedError);
        }
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(initializeAuth, 100);
    return () => clearTimeout(timeoutId);
  }, [authProvider, clearAuthenticatedState, establishAuthenticatedUser]);

  // ── Login ──
  const login = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const result = await authProvider.login();
      tokenService.setAccessToken(result.accessToken);

      // Enrich with backend profile
      const enriched = await enrichWithBackendProfile(result.user);
      establishAuthenticatedUser(enriched);

      sessionStorage.setItem('isAuthenticated', 'true');
    } catch (err) {
      clearAuthenticatedState();
      const normalizedError = normalizeAuthError(err);
      setError(normalizedError);
      throw normalizedError;
    } finally {
      setLoading(false);
    }
  }, [authProvider, clearAuthenticatedState, establishAuthenticatedUser]);

  const getAccessToken = useCallback(async (): Promise<string | null> => {
    try {
      return await authProvider.getAccessToken();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }
  }, [authProvider]);

  // ── Institution context switching (authorized platform users only) ──
  const assumeRole = useCallback(
    (role: UserRole, institutionId: string, institutionName?: string): void => {
      if (!canUseInstitutionSwitcher(accountUser, institutionSwitcherEnabled)) return;
      if (!ASSUMABLE_CONTEXT_ROLES.includes(role)) return;
      const normalizedInstitutionId = normalizeInstitutionContextId(institutionId);
      if (!normalizedInstitutionId) return;

      const normalizedInstitutionName = institutionName?.trim() || null;

      permissionRequestRef.current += 1;
      setPermissions([]);
      setPermissionsLoading(true);
      setAssumedRole(role);
      setAssumedInstitutionId(normalizedInstitutionId);
      setAssumedInstitutionName(normalizedInstitutionName);
      sessionStorage.setItem(ASSUMED_CONTEXT_STORAGE_KEYS.role, role);
      writeStoredInstitutionContextId(
        ASSUMED_CONTEXT_STORAGE_KEYS.institutionId,
        normalizedInstitutionId,
      );
      if (normalizedInstitutionName) {
        sessionStorage.setItem(ASSUMED_CONTEXT_STORAGE_KEYS.institutionName, normalizedInstitutionName);
      } else {
        sessionStorage.removeItem(ASSUMED_CONTEXT_STORAGE_KEYS.institutionName);
      }
    },
    [accountUser, institutionSwitcherEnabled],
  );

  const resetAssumedRole = useCallback((): void => {
    permissionRequestRef.current += 1;
    setAssumedRole(null);
    setAssumedInstitutionId(null);
    setAssumedInstitutionName(null);
    clearAssumedContextStorage();
    setPermissionsLoading(false);
    // Restore original permissions from user object
    if (user) {
      setPermissions(user.permissions || []);
    }
  }, [user]);

  // ── Logout ──
  const enhancedLogout = useCallback(async (options?: LogoutOptions): Promise<void> => {
    try {
      setLoading(true);
      await cancelWorkspaceQueries();
      Object.keys(sessionStorage).forEach(key => {
        if (key.startsWith('permissions_')) {
          sessionStorage.removeItem(key);
        }
      });
      clearAuthenticatedState();
      await authProvider.logout(options ?? { localOnly: true });
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    } finally {
      setLoading(false);
    }
  }, [authProvider, clearAuthenticatedState]);

  // ── Context value ──
  const value: AuthContextValue = {
    user: computedUser,
    originalUser: accountUser,
    loading,
    error,
    login,
    logout: enhancedLogout,
    getAccessToken,
    isAuthenticated: !!user,
    assumedRole,
    assumedInstitutionId,
    assumedInstitutionName,
    assumeRole,
    resetAssumedRole,
    effectiveRole: computedUser?.role || user?.role,
    effectiveInstitutionId: computedUser?.institutionId || user?.institutionId,
    institutionSwitcherEnabled,
    permissions,
    permissionsLoading,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    refetchPermissions: fetchPermissions,
    workspaces,
    workspacesLoading,
    workspaceSwitching,
    workspaceError,
    selectedWorkspaceKey,
    selectWorkspace,
    refetchWorkspaces,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
